import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  confirmDeliveryLocation,
  setDeliveryLocation,
  type DeliveryLocation,
} from "../../../Redux/delivery-location/slice";
import {
  getLocationErrorMessage,
  requestCurrentPosition,
  reverseGeocodeCoordinates,
  searchLocations,
  type LocationSearchResult,
} from "../../../utility/location";


type PickerView = "search" | "confirm";

type DeliveryLocationPickerProps = {
  onClose?: () => void;
  compact?: boolean;
};

const toDeliveryLocation = (
  details: Partial<DeliveryLocation> & { displayName: string },
): DeliveryLocation => ({
  label: details.area || details.city || details.displayName,
  area: details.area,
  city: details.city,
  state: details.state,
  pincode: details.pincode,
  displayName: details.displayName,
  latitude: details.latitude,
  longitude: details.longitude,
});

const DeliveryLocationPicker: React.FC<DeliveryLocationPickerProps> = ({
  onClose,
  compact = false,
}) => {
  const dispatch = useAppDispatch();
  const { location } = useAppSelector((state) => state.deliveryLocation);

  const [view, setView] = useState<PickerView>("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<LocationSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [pendingLocation, setPendingLocation] = useState<DeliveryLocation | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyLocation = useCallback(
    (loc: DeliveryLocation) => {
      dispatch(setDeliveryLocation(loc));
      dispatch(confirmDeliveryLocation());
      onClose?.();
    },
    [dispatch, onClose],
  );

  const handleDetectLocation = async () => {
    try {
      setIsDetecting(true);
      setStatusMessage("Detecting location...");
      const coords = await requestCurrentPosition();
      const details = await reverseGeocodeCoordinates(
        coords.latitude,
        coords.longitude,
      );
      const loc = toDeliveryLocation({
        area: details.area,
        city: details.city,
        state: details.state,
        pincode: details.pincode,
        displayName: details.displayName,
        latitude: details.latitude,
        longitude: details.longitude,
      });
      setPendingLocation(loc);
      setView("confirm");
      setStatusMessage("");
    } catch (error) {
      setStatusMessage(getLocationErrorMessage(error));
    } finally {
      setIsDetecting(false);
    }
  };

  const handleSelectResult = (result: LocationSearchResult) => {
    const displayName = [result.area, result.city, result.state]
      .filter(Boolean)
      .join(", ") || result.displayName;
    applyLocation(
      toDeliveryLocation({
        area: result.area,
        city: result.city,
        state: result.state,
        pincode: result.pincode,
        displayName,
        latitude: result.latitude,
        longitude: result.longitude,
      }),
    );
  };

  const handlePopularClick = async (locality: string) => {
    setSearchQuery(locality);
    setIsSearching(true);
    try {
      const results = await searchLocations(locality);
      if (results.length > 0) {
        handleSelectResult(results[0]);
      } else {
        applyLocation(
          toDeliveryLocation({
            displayName: locality,
            city: locality.split(",")[0]?.trim(),
          }),
        );
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleAccept = () => {
    if (pendingLocation) applyLocation(pendingLocation);
  };

  const handleChange = () => {
    setView("search");
    setPendingLocation(null);
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchLocations(searchQuery);
        setSearchResults(results);
      } finally {
        setIsSearching(false);
      }
    }, 350);

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]);

  if (view === "confirm" && pendingLocation) {
    return (
      <div className={compact ? "" : "p-1"}>
        <div className="d-flex align-items-center gap-1 mb-2">
          <span className="text-xxs text-secondary text-uppercase letter-spacing-1">
            Current location
          </span>
          <i className="bi bi-crosshair text-danger text-sm"></i>
        </div>
        <p className="text-dark fw-bold text-sm mb-3 lh-sm">
          {pendingLocation.displayName}
        </p>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm rounded-pill flex-fill border border-danger text-danger fw-bold"
            onClick={handleChange}
          >
            Change
          </button>
          <button
            type="button"
            className="btn btn-sm rounded-pill flex-fill btn-danger fw-bold"
            onClick={handleAccept}
          >
            Accept
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "" : "p-1"}>
      <div className="input-group input-group-outline mb-3">
        <span className="input-group-text border-0 bg-transparent ps-0">
          <i className="ri-map-pin-2-fill text-danger"></i>
        </span>
        <input
          type="text"
          className="form-control border-0 ps-1 text-sm"
          placeholder="Search location"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus={!compact}
        />
      </div>

      <button
        type="button"
        className="btn btn-outline-danger btn-sm rounded-pill w-100 mb-3"
        onClick={handleDetectLocation}
        disabled={isDetecting}
      >
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-crosshair2 fs-6"></i>
          <div className="text-start lh-sm">
            <div className="text-xs fw-normal text-dark">
              {isDetecting ? "Detecting..." : "Auto-detect current location"}
            </div>
            {statusMessage && (
              <div className="text-xxs text-secondary fw-normal">{statusMessage}</div>
            )}
          </div>
        </div>
      </button>

      {searchQuery.trim().length >= 2 && (
        <div
          className="mb-3"
          style={{ maxHeight: 180, overflowY: "auto" }}
        >
          {isSearching ? (
            <p className="text-xs text-secondary mb-0 px-1">Searching...</p>
          ) : searchResults.length > 0 ? (
            searchResults.map((result) => (
              <button
                key={result.placeId}
                type="button"
                className="btn btn-link text-start w-100 text-decoration-none text-dark p-2 border-0"
                onClick={() => handleSelectResult(result)}
              >
                <span className="text-sm">{result.displayName}</span>
              </button>
            ))
          ) : (
            <p className="text-xs text-secondary mb-0 px-1">No locations found</p>
          )}
        </div>
      )}



      {location?.displayName && compact && (
        <div className="mt-2 pt-2 border-top">
          <p className="text-xxs text-secondary mb-1">Selected</p>
          <p className="text-xs text-dark fw-bold mb-0">{location.displayName}</p>
        </div>
      )}
    </div>
  );
};

export default DeliveryLocationPicker;
